import { DAY_KEYS, type DayKey, isoWeekMonday, isoWeekOf } from "@/lib/days";
import {
  type DayWidgetConfig,
  type DayWidgetWeekEntry,
  emptyDayData,
  emptyWeekData,
  type WeekData,
  type WeekWidgetConfig,
  type WeekWidgetWeekEntry,
  type WidgetConfig,
  type WidgetWeekEntry,
} from "@/lib/schema";
import { getWidgetModule } from "@/widgets/registry";

/**
 * A week is **frozen** once it is past OR completed (docs/adr/0014): the
 * Ukesmal can never be applied to it again, and its snapshot stands as the
 * record of how that week was actually set up.
 *
 * Derived from the week's own dates and state, never stored — which is what
 * makes re-opening a past week unable to thaw it, and what lets "Marker uken
 * som fullført" double as the lever for freezing a week early.
 */
export function isWeekFrozen(week: WeekData, today: Date): boolean {
  if (week.state === "completed") return true;
  const { year, weekNumber } = isoWeekOf(today);
  // Lexicographic comparison is date order for YYYY-MM-DD.
  return week.startDate < isoWeekMonday(year, weekNumber);
}

/** Current or future, and open. The mal can still be applied to it. */
function isWeekUpdatable(week: WeekData, today: Date): boolean {
  return !isWeekFrozen(week, today);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Structural equality over the JSON a config is made of. Key order must not
 * count — a config that round-tripped through storage is the same config —
 * so this cannot be `JSON.stringify` comparison.
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    return a.length === b.length && a.every((x, i) => deepEqual(x, b[i]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((k) => Object.hasOwn(b, k) && deepEqual(a[k], b[k]));
  }
  return false;
}

/**
 * One line of an update modal's per-week change list.
 *
 * `dataDescription` is only ever set on a removal, and only when the entry
 * holds something: it is the cost of the update stated before it happens,
 * since dropping a widget takes its runtime with it and there is no undo.
 */
export interface WidgetChange {
  change: "added" | "changed" | "removed";
  widgetId: string;
  label: string;
  dataDescription: string | null;
}

function describeEntryData(entry: WidgetWeekEntry): string | null {
  const module = getWidgetModule(entry.config.kind);
  return module?.describeWeekData?.(entry) ?? null;
}

/**
 * What applying the mal to this week would do. Computed from
 * `(week.widgets, mal)` alone and never from "the edit you just made", so the
 * answer is the same whether it is asked after one save or after ten — which
 * is what lets one function drive both the sticky bar and the modal.
 */
export function diffWeekAgainstMal(
  week: WeekData,
  mal: WidgetConfig[],
): WidgetChange[] {
  const entries = new Map(week.widgets.map((e) => [e.config.id, e]));
  const changes: WidgetChange[] = [];

  for (const config of mal) {
    const entry = entries.get(config.id);
    if (!entry) {
      changes.push({
        change: "added",
        widgetId: config.id,
        label: config.label,
        dataDescription: null,
      });
      continue;
    }
    if (!deepEqual(entry.config, config)) {
      changes.push({
        change: "changed",
        widgetId: config.id,
        label: config.label,
        dataDescription: null,
      });
    }
  }

  const inMal = new Set(mal.map((w) => w.id));
  for (const entry of week.widgets) {
    if (inMal.has(entry.config.id)) continue;
    changes.push({
      change: "removed",
      widgetId: entry.config.id,
      label: entry.config.label,
      dataDescription: describeEntryData(entry),
    });
  }

  return changes;
}

/**
 * Build a week entry for one config, carrying `previous`'s runtime forward
 * when there is one. Serves both jobs: week creation passes no previous and
 * gets empty runtime, an update passes the matching entry and keeps every
 * checkbox, meal and night already logged into it.
 */
export function buildWeekEntry(
  config: WidgetConfig,
  previous?: WidgetWeekEntry,
): WidgetWeekEntry {
  const cloned = structuredClone(config);
  const module = getWidgetModule(cloned.kind);
  const week = previous?.week ?? emptyWeekData(cloned.kind);
  if (module?.scope === "week") {
    const entry: WeekWidgetWeekEntry = {
      config: cloned as WeekWidgetConfig,
      week,
    };
    return entry;
  }
  const days =
    previous && "days" in previous
      ? previous.days
      : (Object.fromEntries(
          DAY_KEYS.map((k) => [k, emptyDayData(cloned.kind)]),
        ) as Record<DayKey, ReturnType<typeof emptyDayData>>);
  const entry: DayWidgetWeekEntry = {
    config: cloned as DayWidgetConfig,
    week,
    days,
  };
  return entry;
}

/**
 * Make a week's widgets match the mal wholly (docs/adr/0014). Entries are
 * matched by `config.id`: config replaced in place, entries new to the mal
 * added with empty runtime, entries absent from the mal dropped **together
 * with their runtime**. There is no partial application — a week matching
 * neither the mal nor its old snapshot would leave the bar permanently lit.
 */
export function reconcileWeekWidgets(
  entries: WidgetWeekEntry[],
  mal: WidgetConfig[],
): WidgetWeekEntry[] {
  const existing = new Map(entries.map((e) => [e.config.id, e]));
  return mal.map((config) => buildWeekEntry(config, existing.get(config.id)));
}

export interface WeekRecord {
  id: string;
  data: WeekData;
}

export interface WeekUpdateCandidate extends WeekRecord {
  changes: WidgetChange[];
}

/**
 * The updatable weeks whose snapshot differs from the mal — the modal's rows,
 * and, when non-empty, the reason the sticky bar is on screen.
 */
export function findWeekUpdateCandidates(
  weeks: WeekRecord[],
  mal: WidgetConfig[],
  today: Date,
): WeekUpdateCandidate[] {
  return weeks
    .filter((w) => isWeekUpdatable(w.data, today))
    .map((w) => ({ ...w, changes: diffWeekAgainstMal(w.data, mal) }))
    .filter((w) => w.changes.length > 0)
    .sort((a, b) => a.data.startDate.localeCompare(b.data.startDate));
}
